import { getRequestDate } from '../utils/dateUtils';
import { genderOptions, sexOptions } from '../configs/users';
import { api, resolvePagination } from './api';

/**
 * Retrieve current user settings
 */
export const getUser = async () => api.get('/cheart/user');

/**
 * set the profile picture for a user.
 * @param {Blob} image - The image file or blob to upload as the user's picture.
 */
export const postUserPicture = async (image) =>
    api.post(
        '/cheart/user/picture',
        {
            picture: image
        },
        {
            headers: {
                'content-type': 'multipart/form-data'
            }
        }
    );

/**
 * Retrieve list of ConnectedHeart users.
 * @param {Array} [types=[]] - An array of user types to filter the results.
 * @param {number} pageLimit - The maximum number of users to retrieve per page.
 * @param {string} pageToken - The token for the next page of results.
 * @param {string} query - An optional search query to filter users.
 */
export const getUsers = async (types = [], pageLimit, pageToken, query, clinicUuid) => {
    const headers = resolvePagination(pageToken, pageLimit);

    headers['CHEART-QUERY-ROLE'] = JSON.stringify(types);

    if (clinicUuid) {
        headers['CHEART-QUERY-CLINIC'] = clinicUuid;
    }

    if (query) {
        return api.post('/cheart/users/search', { query }, { headers });
    }

    return api.get('/cheart/users', { headers });
};

const roleMap = {
    0: 'PATIENT',
    1: 'DOCTOR',
    2: 'CLINIC_ADMIN',
    3: 'CLINIC_CARE_GIVER',
    4: 'PATIENT_CARE_GIVER'
};

/**
 * create a new ConnectedHeart user
 * @param {object} values
 */
export const postUser = async (values, role_uuid = null) => {
    const payload = {
        name: values.name,
        email: values.email,
        role: roleMap[values.type] ?? 'PATIENT',
        account: values.clinic_id,
        clinic_id: values.clinic_id,
        sex: values.sex ? sexOptions[values.sex.value] : null,
        gender: values.gender ? genderOptions[values.gender.value] : null,
        birth_date: values.birthdate ? getRequestDate(values.birthdate) : null,
        health_id: values.healthSystemNumber,
        health_id_type: '',
        address: {
            street: values.address,
            zip: values.zip,
            city: values.city,
            province: null,
            country: null
        },
        phone: values.phone.toString(),
        user_data: {
            maxHeartRate: values.maxHeartRate,
            trainingHeartRate: values.trainingHeartRate,
            minTrainingHeartRate: values.minTrainingHeartRate,
            restingHeartRate: values.restingHeartRate,
            anaerobicThreshold: values.anaerobicThreshold,
            ventilatoryThreshold: values.ventilatoryThreshold,
            professionalCertificate: values.professionalCertificate,
            speciality: values.speciality,
            height: values.height,
            weight: values.weight,
            vo2Max: values.vo2Max,
            trainingBackgorund: values.trainingBackgorund,
            typicalDay: values.typicalDay,
            sleepGoalMinutes: values.sleepGoalMinutes
        }
    };

    if (role_uuid) {
        payload.role_uuid = role_uuid;
    }

    return api.post('/cheart/users', payload);
};

/**
 * patch a new ConnectedHeart user
 * @param {object} values
 */
export const patchUser = async (values, role_uuid) => {
    const payload = {
        role_uuid,
        name: values.name,
        sex: values.sex ? sexOptions[values.sex.value] : null,
        gender: values.gender ? genderOptions[values.gender.value] : null,
        birth_date: values.birthdate ? getRequestDate(values.birthdate) : null,
        address: {
            street: values.address,
            zip: values.zip,
            city: values.city,
            province: null,
            country: null
        },
        phone: values.phone?.toString(),
        user_data: {
            maxHeartRate: values.maxHeartRate,
            trainingHeartRate: values.trainingHeartRate,
            minTrainingHeartRate: values.minTrainingHeartRate,
            restingHeartRate: values.restingHeartRate,
            anaerobicThreshold: values.anaerobicThreshold,
            ventilatoryThreshold: values.ventilatoryThreshold,
            professionalCertificate: values.professionalCertificate,
            speciality: values.speciality,
            height: values.height,
            weight: values.weight,
            vo2Max: values.vo2Max,
            trainingBackgorund: values.trainingBackgorund,
            typicalDay: values.typicalDay,
            sleepGoalMinutes: values.sleepGoalMinutes
        }
    };

    return api.patch('/cheart/users', payload);
};

/**
 * Delete (disable) a ConnectedHeart user.
 * @param {string} uuid - The unique identifier of the user to delete.
 */
export const deleteUser = async (uuid) =>
    api.delete('/cheart/users', {
        data: {
            role_uuid: uuid
        }
    });

/**
 * Retrieve ConnectedHealth user by uuid
 * @param {string} uuid - The role UUID of the user to fetch.
 */
export const fetchUser = async (uuid) =>
    api.post('/cheart/users/fetch', {
        role_uuid: uuid
    });

/**
 * Reset ConnectedHeart user password.
 * @param {string} uuid - The unique identifier of the user whose password is to be deleted.
 */
export const resetUserPassword = async (uuid) =>
    api.delete('/cheart/users/password', {
        data: {
            role_uuid: uuid
        }
    });

/**
 * Get groups for user.
 * @param {string} uuid - The role UUID of the user.
 */
export const getUserGroups = async (uuid) => {
    const response = await api.post('/cheart/user/groups', { uuid });
    return response.data;
};
